import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  Copy,
  Download,
  Pause,
  Pencil,
  Play,
  Plus,
  QrCode,
  RefreshCw,
  X,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import {
  buildQrSvg,
  downloadBlob,
  isProductionSite,
  patientSiteUrl,
  shortLinkUrl,
  svgToPngBlob,
} from '@/lib/qrCode';
import { api } from '@/services/api';
import { CampaignLink, CampaignLinkInput } from '@/types';

/**
 * QR codes for print: each holds a short link (voyaxhealth.com/go/<code>).
 * Where the link goes, its UTM tags and its partner pharmacy are stored in
 * the database (campaign_links), so they can change, or the code can be
 * paused, after printing. Results come from scans (web_events) and from
 * accounts whose first visit was the code (account_acquisition).
 */

const DESTINATIONS: { label: string; path: string }[] = [
  { label: 'Homepage', path: '/' },
  { label: 'Start a trip', path: '/trip/' },
  { label: 'Field guide', path: '/field-guide/' },
  { label: 'Hendricks Pharmacy page', path: '/hendricks/' },
  { label: '986 Pharmacy page', path: '/986-pharmacy/' },
];

const PARTNERS: { value: string; label: string }[] = [
  { value: 'hendricks', label: 'Hendricks Pharmacy' },
  { value: '986-pharmacy', label: '986 Pharmacy' },
];

const WINDOWS: { value: number | 'all'; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 30, label: 'Last 30 days' },
  { value: 7, label: 'Last 7 days' },
];

const EMPTY: CampaignLinkInput = {
  slug: '',
  name: '',
  destination: '/trip/',
  utmSource: '',
  utmMedium: 'qr',
  utmCampaign: '',
  utmContent: null,
  partner: null,
  notes: null,
};

/** Same rules as the API: lowercase, digits, underscores. */
function cleanTag(value: string): string {
  return value.toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function formatMoney(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

async function downloadQr(slug: string, format: 'png' | 'svg') {
  const svg = buildQrSvg(shortLinkUrl(slug));
  const blob = format === 'svg' ? new Blob([svg], { type: 'image/svg+xml' }) : await svgToPngBlob(svg);
  downloadBlob(blob, `voyax-qr-${slug}.${format}`);
}

function QrPreview({ slug, size = 176 }: { slug: string; size?: number }) {
  const svg = useMemo(() => (slug ? buildQrSvg(shortLinkUrl(slug)) : ''), [slug]);
  if (!svg) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-dashed border-gray-300 text-xs text-gray-400"
        style={{ width: size, height: size }}
      >
        Enter a code
      </div>
    );
  }
  return (
    <img
      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`}
      width={size}
      height={size}
      alt={`QR code for ${shortLinkUrl(slug)}`}
      className="rounded-lg border border-gray-200"
    />
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard blocked */
        }
      }}
      className="inline-flex items-center text-gray-400 hover:text-gray-700"
      title="Copy link"
      aria-label="Copy link"
    >
      {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
    </button>
  );
}

function LinkEditor({
  initial,
  existing,
  onCancel,
  onSaved,
}: {
  initial: CampaignLinkInput;
  existing: CampaignLink | null;
  onCancel: () => void;
  onSaved: (link: CampaignLink) => void;
}) {
  const [form, setForm] = useState<CampaignLinkInput>(initial);
  const [slugTouched, setSlugTouched] = useState(!!existing);
  const [customDestination, setCustomDestination] = useState(
    !DESTINATIONS.some((d) => d.path === initial.destination),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const slug = existing ? existing.slug : form.slug || '';

  const set = <K extends keyof CampaignLinkInput>(key: K, value: CampaignLinkInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body: CampaignLinkInput = {
        ...form,
        utmContent: form.utmContent || null,
        partner: form.partner || null,
        notes: form.notes || null,
      };
      const saved: CampaignLink = existing
        ? await api.updateCampaignLink(existing.id, { ...body, slug: undefined })
        : await api.createCampaignLink(body);
      onSaved(saved);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Could not save the QR code');
    } finally {
      setSaving(false);
    }
  };

  const input =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
  const label = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">{existing ? `Edit “${existing.name}”` : 'New QR code'}</h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-700" aria-label="Close">
          <X size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={label} htmlFor="qr-name">Name (for your team)</label>
              <input
                id="qr-name"
                className={input}
                value={form.name}
                placeholder="VFS Global SFO — front counter"
                onChange={(e) => {
                  const name = e.target.value;
                  setForm((f) => ({ ...f, name, ...(!slugTouched && !existing ? { slug: slugify(name) } : {}) }));
                }}
              />
            </div>
            <div>
              <label className={label} htmlFor="qr-slug">Code (in the printed link)</label>
              <input
                id="qr-slug"
                className={`${input} ${existing ? 'bg-gray-50 text-gray-500' : ''}`}
                value={slug}
                disabled={!!existing}
                placeholder="vfs-sfo-counter"
                onChange={(e) => {
                  setSlugTouched(true);
                  set('slug', slugify(e.target.value));
                }}
              />
              <p className="mt-1 text-xs text-gray-500">
                {existing ? 'Fixed once created, so printed codes keep working.' : 'Lowercase letters, numbers and hyphens.'}
              </p>
            </div>
          </div>

          <div>
            <label className={label} htmlFor="qr-destination">Sends people to</label>
            <div className="flex flex-col md:flex-row gap-2">
              <select
                id="qr-destination"
                className={`${input} md:max-w-xs`}
                value={customDestination ? 'custom' : form.destination}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setCustomDestination(true);
                  } else {
                    setCustomDestination(false);
                    set('destination', e.target.value);
                  }
                }}
              >
                {DESTINATIONS.map((d) => (
                  <option key={d.path} value={d.path}>
                    {d.label} ({d.path})
                  </option>
                ))}
                <option value="custom">Another page…</option>
              </select>
              {customDestination && (
                <input
                  className={input}
                  value={form.destination}
                  placeholder="/field-guide/country/thailand/"
                  onChange={(e) => set('destination', e.target.value.trim())}
                />
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">A page on {patientSiteUrl().replace(/^https:\/\//, '')}. You can change this later.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className={label} htmlFor="qr-source">Source</label>
              <input id="qr-source" className={input} value={form.utmSource} placeholder="vfs"
                onChange={(e) => set('utmSource', cleanTag(e.target.value))} />
            </div>
            <div>
              <label className={label} htmlFor="qr-medium">Medium</label>
              <input id="qr-medium" className={input} value={form.utmMedium} placeholder="qr"
                onChange={(e) => set('utmMedium', cleanTag(e.target.value))} />
            </div>
            <div>
              <label className={label} htmlFor="qr-campaign">Campaign</label>
              <input id="qr-campaign" className={input} value={form.utmCampaign} placeholder="sfo_office"
                onChange={(e) => set('utmCampaign', cleanTag(e.target.value))} />
            </div>
            <div>
              <label className={label} htmlFor="qr-content">Content (optional)</label>
              <input id="qr-content" className={input} value={form.utmContent ?? ''} placeholder="counter_sign"
                onChange={(e) => set('utmContent', cleanTag(e.target.value) || null)} />
            </div>
          </div>
          <p className="-mt-2 text-xs text-gray-500">
            Source = who sent them · Medium = qr for printed codes · Campaign = the placement. Lowercase and underscores
            only, so results group together (see docs/UTM_Links.md).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={label} htmlFor="qr-partner">Partner pharmacy (optional)</label>
              <select id="qr-partner" className={input} value={form.partner ?? ''}
                onChange={(e) => set('partner', e.target.value || null)}>
                <option value="">None</option>
                {PARTNERS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">Preselects this pharmacy for anyone who scans.</p>
            </div>
            <div>
              <label className={label} htmlFor="qr-notes">Notes (optional)</label>
              <input id="qr-notes" className={input} value={form.notes ?? ''} placeholder="Where it's printed, how many"
                onChange={(e) => set('notes', e.target.value)} />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
            >
              {saving ? 'Saving…' : existing ? 'Save changes' : 'Create QR code'}
            </button>
            <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
              Cancel
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3">
          <QrPreview slug={slug} size={208} />
          <div className="flex items-center gap-1.5 text-xs text-gray-600 max-w-[13rem] break-all text-center">
            {slug ? shortLinkUrl(slug) : '—'}
          </div>
          {!existing && <p className="text-xs text-gray-400 text-center max-w-[13rem]">Download after creating it.</p>}
        </div>
      </div>
    </div>
  );
}

export default function QrCodesPage() {
  const { user } = useAuth();
  const canEdit = !!user?.roles?.includes('admin');
  const [links, setLinks] = useState<CampaignLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState<number | 'all'>('all');
  const [editing, setEditing] = useState<{ link: CampaignLink | null } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await api.getCampaignLinks({ days });
      setLinks(res.links ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load QR codes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const toggleActive = async (link: CampaignLink) => {
    setBusyId(link.id);
    try {
      const updated: CampaignLink = await api.updateCampaignLink(link.id, { isActive: !link.isActive });
      setLinks((all) => all.map((l) => (l.id === link.id ? { ...updated, stats: l.stats } : l)));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Could not update the QR code');
    } finally {
      setBusyId(null);
    }
  };

  const editorInitial = (link: CampaignLink | null): CampaignLinkInput =>
    link
      ? {
          name: link.name,
          destination: link.destination,
          utmSource: link.utmSource,
          utmMedium: link.utmMedium,
          utmCampaign: link.utmCampaign,
          utmContent: link.utmContent,
          partner: link.partner,
          notes: link.notes,
        }
      : EMPTY;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">QR Codes</h1>
          <p className="text-gray-500 mt-1">
            Printable codes with UTM tracking. Change where a code goes, or pause it, without reprinting.
          </p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <select
            value={String(days)}
            onChange={(e) => setDays(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            aria-label="Results window"
          >
            {WINDOWS.map((w) => (
              <option key={String(w.value)} value={String(w.value)}>{w.label}</option>
            ))}
          </select>
          <button
            onClick={load}
            className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            aria-label="Refresh"
          >
            <RefreshCw size={16} />
          </button>
          {canEdit && (
            <button
              onClick={() => setEditing({ link: null })}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
            >
              <Plus size={16} className="mr-1.5" />
              New QR code
            </button>
          )}
        </div>
      </div>

      {!isProductionSite() && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 mb-6 text-sm text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <span>
            This is the <strong>{patientSiteUrl().replace(/^https:\/\//, '')}</strong> admin. Codes made here point at
            that test site. Make codes for printing in the production admin.
          </span>
        </div>
      )}

      {editing && (
        <LinkEditor
          key={editing.link?.id ?? 'new'}
          initial={editorInitial(editing.link)}
          existing={editing.link}
          onCancel={() => setEditing(null)}
          onSaved={(saved) => {
            setLinks((all) => {
              const old = all.find((l) => l.id === saved.id);
              const merged = { ...saved, stats: old?.stats ?? { scans: 0, scanners: 0, lastScanAt: null, accounts: 0, paid: 0, revenue: 0 } };
              return old ? all.map((l) => (l.id === saved.id ? merged : l)) : [merged, ...all];
            });
            setEditing(null);
          }}
        />
      )}

      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
          <AlertCircle size={20} className="text-red-500" />
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : links.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <QrCode className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No QR codes yet</h3>
          <p className="mt-2 text-gray-500">
            {canEdit ? 'Create one to get a printable code with tracking.' : 'An admin can create one.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {links.map((link) => {
            const stats = link.stats;
            const url = shortLinkUrl(link.slug);
            return (
              <div key={link.id} className={`bg-white rounded-lg shadow-sm p-5 ${link.isActive ? '' : 'opacity-75'}`}>
                <div className="flex flex-col lg:flex-row gap-5">
                  <QrPreview slug={link.slug} size={112} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{link.name}</h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          link.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {link.isActive ? 'Active' : 'Paused — sends to homepage'}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-sm text-primary-700">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline break-all">
                        {url.replace(/^https:\/\//, '')}
                      </a>
                      <CopyButton text={url} />
                    </div>
                    <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                      <div>
                        <dt className="inline text-gray-400">Goes to </dt>
                        <dd className="inline">{link.destination}</dd>
                      </div>
                      <div>
                        <dt className="inline text-gray-400">Tags </dt>
                        <dd className="inline">
                          {[link.utmSource, link.utmMedium, link.utmCampaign, link.utmContent].filter(Boolean).join(' / ')}
                        </dd>
                      </div>
                      {link.partner && (
                        <div>
                          <dt className="inline text-gray-400">Partner </dt>
                          <dd className="inline">{PARTNERS.find((p) => p.value === link.partner)?.label ?? link.partner}</dd>
                        </div>
                      )}
                      {link.notes && (
                        <div className="sm:col-span-2">
                          <dt className="inline text-gray-400">Notes </dt>
                          <dd className="inline">{link.notes}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  <div className="grid grid-cols-5 gap-3 text-center lg:w-[26rem] self-center">
                    {[
                      ['Scans', stats?.scans ?? 0],
                      ['People', stats?.scanners ?? 0],
                      ['Accounts', stats?.accounts ?? 0],
                      ['Paid', stats?.paid ?? 0],
                      ['Revenue', formatMoney(stats?.revenue ?? 0)],
                    ].map(([title, value]) => (
                      <div key={String(title)}>
                        <div className="text-lg font-semibold text-gray-900 tabular-nums">
                          {typeof value === 'number' ? value.toLocaleString() : value}
                        </div>
                        <div className="text-[11px] uppercase tracking-wide text-gray-500">{title}</div>
                      </div>
                    ))}
                    <div className="col-span-5 text-[11px] text-gray-400">Last scan {formatDate(stats?.lastScanAt)}</div>
                  </div>

                  <div className="flex lg:flex-col gap-2 self-center">
                    <button
                      onClick={() => downloadQr(link.slug, 'png')}
                      className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <Download size={14} className="mr-1" /> PNG
                    </button>
                    <button
                      onClick={() => downloadQr(link.slug, 'svg')}
                      className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50"
                      title="Vector file for printers"
                    >
                      <Download size={14} className="mr-1" /> SVG
                    </button>
                    {canEdit && (
                      <>
                        <button
                          onClick={() => setEditing({ link })}
                          className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50"
                        >
                          <Pencil size={14} className="mr-1" /> Edit
                        </button>
                        <button
                          onClick={() => toggleActive(link)}
                          disabled={busyId === link.id}
                          className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                          {link.isActive ? <Pause size={14} className="mr-1" /> : <Play size={14} className="mr-1" />}
                          {link.isActive ? 'Pause' : 'Resume'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <p className="text-xs text-gray-400">
            Scans exclude bots and team browsers. Accounts, Paid and Revenue count people whose first visit was this code.
          </p>
        </div>
      )}
    </div>
  );
}
